/**
 * The "how to run a game" copy, in one place. It is shown twice — as the first-run card
 * in the panel, and on demand from the `?` button / the ⋯ menu — so edit it here and both
 * appearances follow.
 */

import type { MapId, Variant } from '../engine/types.js';

export const HELP_TITLE = 'Running a game here';

/** The power each map's examples are written for: [name, chip letter, a build]. */
const EXAMPLE: Record<MapId, { power: string; letter: string; build: string }> = {
  standard: { power: 'England', letter: 'E', build: 'Build F Edi' },
  golarion: { power: 'Cheliax', letter: 'C', build: 'Build F Wcr' },
};

export interface HelpStep {
  text: string;
  /** Optional nested bullets, rendered under the step in both the card and the modal. */
  sub?: string[];
}

export function helpSteps(map: MapId = 'standard'): HelpStep[] {
  const ex = EXAMPLE[map];
  return [
  { text: 'Name the game (top left) — it gets drawn onto exported images.' },
  {
    text: 'Enter the turn’s orders. Three ways, and they mix freely:',
    sub: [
      `Paste everyone’s orders into the All tab, with \`${ex.power}:\` style headers.`,
      'Or put each player’s orders in that player’s own power tab.',
      'Or click a unit on the board, then its destination.',
    ],
  },
  {
    text:
      'Read the readiness strip. Each power’s chip shows how many of that player’s units have ' +
      `been given orders out of how many they have — “${ex.letter} 3/5” (${ex.power}: three of five units ordered) ` +
      'means the other two will hold. A red badge counts lines that didn’t parse; click a chip for ' +
      'the detail.',
  },
  { text: 'Fix anything flagged red, then Adjudicate — if problems remain it lists them and asks first.' },
  { text: 'Send the results text and the board image to your players.' },
  ];
}

export const HELP_ALSO_TITLE = 'Also';

export function helpAlso(map: MapId = 'standard', variant: Variant = 'conway'): string[] {
  const ex = EXAMPLE[map];
  const life = variant !== 'standard';
  const pick =
    'Pick any entry from the dropdown, or step through them with ↶ and ↷; "Current" is the live board. Looking is read-only — nothing in the history bar changes the game.';
  return [
    ...(life
      ? [
          `Spawn choices — when the Life step births a unit on a coast, its owner picks army or fleet. Use the buttons, or type the decisions like builds (\`${ex.power}: ${ex.build}\`). Undecided births become armies.`,
          `History — every phase, and every Life step, is its own entry. Each shows the board its orders or marks acted on, so a bounce ✕ sits where the move was stopped and a Life ✕ sits on the unit that died. ${pick}`,
        ]
      : [
          `History — every phase is its own entry. Each shows the board its orders acted on, so a bounce ✕ sits where the move was stopped. ${pick}`,
        ]),
    'Undo — in the ⋯ menu, or ⌘Z (redo is ⇧⌘Z). It takes back the last adjudication itself: the orders come back to the box and the result is discarded, so it asks first.',
    'Share link — hands anyone an independent copy of the board to experiment with. Nothing they do there affects your game.',
    'Export / Import JSON — saves or restores a whole game, history included. Use it to back a game up, or to move it to another browser.',
  ];
}
